"use client"

import { useRef, useState } from "react"
import moment from "moment"

const StatusModel = ({ statusPopup, onClose }) => {
  const receiptRef = useRef(null)
  const [isGenerating, setIsGenerating] = useState(false)

  const receiptData = {
    name: statusPopup?.name,
    phone: statusPopup?.number,
    application: statusPopup?.uniqueId,
    address: statusPopup?.address,
    description: statusPopup?.description,
    amount: statusPopup?.amount,
    paymentMethod: statusPopup?.paymentMethod,
    countryOfInterest:statusPopup.countryOfInterest,
    visaCategory:statusPopup.visaCategory,
    date: moment(statusPopup?.createdAt).format("MM/DD/YY"),
    time: moment(statusPopup?.createdAt).format("h:mma"),
    paymentStatus: statusPopup?.paymentStatus,
  }

  const handleDownload = async () => {
    if (!receiptRef.current || isGenerating) return

    setIsGenerating(true)

    try {
      // Dynamically import the libraries only when needed
      const [{ jsPDF }, { default: html2canvas }] = await Promise.all([import("jspdf"), import("html2canvas")])

      // Clone the receipt element to modify it for PDF generation
      const element = receiptRef.current.cloneNode(true)

      // Set explicit dimensions and styles for the clone
      element.style.width = "800px"
      element.style.padding = "60px"
      element.style.backgroundColor = "white"
      element.classList.add("pdf-only")
      // Temporarily append to body but hide it
      element.style.position = "absolute"
      element.style.left = "-9999px"
      document.body.appendChild(element)

      // Generate canvas with optimized settings
      const canvas = await html2canvas(element, {
        scale: 2, // Balance between quality and performance
        logging: false, 
        useCORS: true,
        backgroundColor: "#ffffff",
        allowTaint: true,
        imageTimeout: 15000, // Increase timeout for images
      })

      // Remove the temporary element
      document.body.removeChild(element)

      // Create PDF with letter size
      const imgData = canvas.toDataURL("image/jpeg", 0.95)
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      })

      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width

      pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight)
      pdf.save("e-Visa-Receipt.pdf")
    } catch (error) {
      console.error("Error generating PDF:", error)
      alert("There was an error generating the PDF. Please try again.")
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <div className="fixed top-0 left-0 w-full h-full bg-black/20 flex justify-center items-center z-50">
      <div className="bg-white w-[800px] p-6 pb-12 rounded-md shadow-lg relative">
        {/* PDF content only */}
        <div ref={receiptRef} className="pdf-content">
          <div className="flex justify-between items-start text-sm text-gray-600 mb-3">
            <span>e-Visa - Payment Receipt</span>
            <span>
              {receiptData.date} - {receiptData.time}
            </span>
          </div>

          {/* Use next/image for better optimization */}
          <div className="w-40 h-16 flex mb-4">
            <img src="/logo.png" alt="Logo" className="w-full h-auto object-contain" crossOrigin="anonymous" />
          </div>

          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-normal">{receiptData.countryOfInterest || "- -"} Visa Application Fee</h2>
            <span className="text-lg font-normal">{receiptData.visaCategory || "- -"}</span>
          </div>

          <div className="border border-gray-300 p-4 mb-4">
            <div className="grid grid-cols-2 gap-4 mb-2">
              <div>
                <p className="pdf-label">Name</p>
                <p className="pdf-value overflow-hidden">{receiptData.name}</p>
              </div>
              <div>
                <p className="pdf-label">Phone</p>
                <p className="pdf-value overflow-hidden">{receiptData.phone}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-2">
              <div>
                <p className="pdf-label">Application</p>
                <p className="pdf-value overflow-hidden">{receiptData.application}</p>
              </div>
              <div>
                <p className="pdf-label">Address</p>
                <p className="pdf-value overflow-hidden">{receiptData.address}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-2">
              <div>
                <p className="pdf-label">Description</p>
                <p className="pdf-value overflow-hidden">{receiptData.description}</p>
              </div>
              <div>
                <p className="pdf-label">Amount</p>
                <p className="pdf-value overflow-hidden">{receiptData.amount}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-2">
              <div>
                <p className="pdf-label">Method of Payment</p>
                <p className="pdf-value overflow-hidden">{receiptData.paymentMethod}</p>
              </div>
              <div>
                <p className="pdf-label">Payment Status</p>
                <p className="pdf-value overflow-hidden">{receiptData.paymentStatus}</p>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-600 mb-2">This is a computer generated Invoice. No signature is required</p>
        </div>

        {/* Buttons (excluded from PDF) */}
        <div className="absolute bottom-4 right-4 flex gap-3">
          <button
            onClick={onClose}
            className="bg-[#005787] text-white px-6 py-1.5 rounded-full"
            disabled={isGenerating}
          >
            Close
          </button>
          <button
            onClick={handleDownload}
            className="bg-[#005787] text-white px-6 py-1.5 rounded-full flex items-center justify-center gap-2"
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <div className="loader-small mr-2"></div>
                <span>Generating...</span>
              </>
            ) : (
              <span>Download</span>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default StatusModel
